import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Toast from '../../components/UI/Toast';

interface CompanyDetails {
    registrationNumber: string;
    gstNumber?: string;
    panNumber: string;
    address: {
        street?: string;
        city?: string;
        state?: string;
        pincode?: string;
        country: string;
    };
    website?: string;
    description?: string;
    industry?: string;
    companySize?: '1-10' | '11-50' | '51-200' | '201-500' | '500+';
}

interface ContactPerson {
    name: string;
    designation?: string;
    email: string;
    phone: string;
}

interface Company {
    _id: string;
    companyName: string;
    email: string;
    phone: string;
    companyDetails: CompanyDetails;
    contactPerson: ContactPerson;
    logo?: string;
    isActive: boolean;
    isVerified: boolean;
    subscription: {
        plan: 'basic' | 'premium' | 'enterprise';
        startDate?: string;
        endDate?: string;
        isActive: boolean;
    };
    settings: {
        allowJobPosting: boolean;
        maxJobPosts: number;
        allowTeamManagement: boolean;
        notificationSettings: {
            emailNotifications: boolean;
            smsNotifications: boolean;
        };
    };
    totalJobsPosted: number;
    totalEmployees: number;
    createdAt: string;
    updatedAt: string;
}

const CompanyProfile: React.FC = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.auth);
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');

    const [formData, setFormData] = useState({
        companyName: '',
        phone: '',
        companyDetails: {
            registrationNumber: '',
            gstNumber: '',
            panNumber: '',
            address: {
                street: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India'
            },
            website: '',
            description: '',
            industry: '',
            companySize: '1-10' as const
        },
        contactPerson: {
            name: '',
            designation: '',
            email: '',
            phone: ''
        },
        settings: {
            allowJobPosting: true,
            maxJobPosts: 10,
            allowTeamManagement: true,
            notificationSettings: {
                emailNotifications: true,
                smsNotifications: false
            }
        }
    });

    const fetchCompanyProfile = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(`/companies/${user?.id}`);
            const companyData = response.data.data;
            setCompany(companyData);
            setFormData({
                companyName: companyData.companyName || '',
                phone: companyData.phone || '',
                companyDetails: {
                    registrationNumber: companyData.companyDetails?.registrationNumber || '',
                    gstNumber: companyData.companyDetails?.gstNumber || '',
                    panNumber: companyData.companyDetails?.panNumber || '',
                    address: {
                        street: companyData.companyDetails?.address?.street || '',
                        city: companyData.companyDetails?.address?.city || '',
                        state: companyData.companyDetails?.address?.state || '',
                        pincode: companyData.companyDetails?.address?.pincode || '',
                        country: companyData.companyDetails?.address?.country || 'India'
                    },
                    website: companyData.companyDetails?.website || '',
                    description: companyData.companyDetails?.description || '',
                    industry: companyData.companyDetails?.industry || '',
                    companySize: companyData.companyDetails?.companySize || '1-10'
                },
                contactPerson: {
                    name: companyData.contactPerson?.name || '',
                    designation: companyData.contactPerson?.designation || '',
                    email: companyData.contactPerson?.email || '',
                    phone: companyData.contactPerson?.phone || ''
                },
                settings: {
                    allowJobPosting: companyData.settings?.allowJobPosting ?? true,
                    maxJobPosts: companyData.settings?.maxJobPosts ?? 10,
                    allowTeamManagement: companyData.settings?.allowTeamManagement ?? true,
                    notificationSettings: {
                        emailNotifications: companyData.settings?.notificationSettings?.emailNotifications ?? true,
                        smsNotifications: companyData.settings?.notificationSettings?.smsNotifications ?? false
                    }
                }
            });
            if (companyData.logo) {
                // Use relative path to avoid CORS issues
                const logoUrl = companyData.logo.startsWith('http')
                    ? companyData.logo
                    : companyData.logo;
                setLogoPreview(logoUrl);
            }
        } catch (error: any) {
            console.error('Error fetching company profile:', error);
            dispatch(showToast({ message: 'Failed to load company profile', type: 'error' }));
        } finally {
            setLoading(false);
        }
    }, [user?.id, dispatch]);

    useEffect(() => {
        fetchCompanyProfile();
    }, [fetchCompanyProfile]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        if (name.includes('.')) {
            const [parent, child, grandchild] = name.split('.');
            setFormData(prev => {
                const parentObj = prev[parent as keyof typeof prev] as any;
                return {
                    ...prev,
                    [parent]: {
                        ...parentObj,
                        [child]: grandchild ? {
                            ...parentObj[child],
                            [grandchild]: type === 'checkbox' ? checked : value
                        } : (type === 'checkbox' ? checked : value)
                    }
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadLogo = async () => {
        if (!logoFile) return null;

        const formData = new FormData();
        formData.append('logo', logoFile);

        try {
            const response = await api.post('/upload/logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.logoPath;
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw error;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        try {
            let logoPath = company?.logo;
            if (logoFile) {
                logoPath = await uploadLogo();
            }

            const updateData = {
                ...formData,
                logo: logoPath
            };

            const response = await api.put(`/companies/${user?.id}`, updateData);
            setCompany(response.data.data);

            // Update logo preview with the new logo URL
            if (logoPath) {
                const logoUrl = logoPath.startsWith('http')
                    ? logoPath
                    : logoPath;
                setLogoPreview(logoUrl);
            }

            // Reset file input
            setLogoFile(null);

            dispatch(showToast({ message: 'Company profile updated successfully', type: 'success' }));
        } catch (error: any) {
            console.error('Error updating company profile:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update company profile',
                type: 'error'
            }));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Company Profile</h1>
                        <p className="text-blue-100 mt-1">
                            Manage your company settings and information
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${company?.isVerified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {company?.isVerified ? 'Verified' : 'Pending Verification'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${company?.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {company?.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Basic Information</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Company Name *
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Phone Number *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                pattern="[6-9][0-9]{9}"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                value={company?.email || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                            />
                            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Company Logo
                            </label>
                            <div className="flex items-center space-x-4">
                                {logoPreview && (
                                    <img
                                        src={logoPreview}
                                        alt="Company Logo"
                                        className="w-16 h-16 object-cover rounded-lg border border-gray-300"
                                    />
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Company Details */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Company Details</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Registration Number *
                            </label>
                            <input
                                type="text"
                                name="companyDetails.registrationNumber"
                                value={formData.companyDetails.registrationNumber}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                PAN Number *
                            </label>
                            <input
                                type="text"
                                name="companyDetails.panNumber"
                                value={formData.companyDetails.panNumber}
                                onChange={handleInputChange}
                                required
                                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                GST Number
                            </label>
                            <input
                                type="text"
                                name="companyDetails.gstNumber"
                                value={formData.companyDetails.gstNumber}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Industry
                            </label>
                            <input
                                type="text"
                                name="companyDetails.industry"
                                value={formData.companyDetails.industry}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Company Size
                            </label>
                            <select
                                name="companyDetails.companySize"
                                value={formData.companyDetails.companySize}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="1-10">1-10 employees</option>
                                <option value="11-50">11-50 employees</option>
                                <option value="51-200">51-200 employees</option>
                                <option value="201-500">201-500 employees</option>
                                <option value="500+">500+ employees</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Website
                            </label>
                            <input
                                type="url"
                                name="companyDetails.website"
                                value={formData.companyDetails.website}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Company Description
                        </label>
                        <textarea
                            name="companyDetails.description"
                            value={formData.companyDetails.description}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>

                {/* Address */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Address</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Street Address
                            </label>
                            <input
                                type="text"
                                name="companyDetails.address.street"
                                value={formData.companyDetails.address.street}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                City
                            </label>
                            <input
                                type="text"
                                name="companyDetails.address.city"
                                value={formData.companyDetails.address.city}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                State
                            </label>
                            <input
                                type="text"
                                name="companyDetails.address.state"
                                value={formData.companyDetails.address.state}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Pincode
                            </label>
                            <input
                                type="text"
                                name="companyDetails.address.pincode"
                                value={formData.companyDetails.address.pincode}
                                onChange={handleInputChange}
                                pattern="[1-9][0-9]{5}"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Country
                            </label>
                            <input
                                type="text"
                                name="companyDetails.address.country"
                                value={formData.companyDetails.address.country}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Contact Person */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Contact Person</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Name *
                            </label>
                            <input
                                type="text"
                                name="contactPerson.name"
                                value={formData.contactPerson.name}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Designation
                            </label>
                            <input
                                type="text"
                                name="contactPerson.designation"
                                value={formData.contactPerson.designation}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Email *
                            </label>
                            <input
                                type="email"
                                name="contactPerson.email"
                                value={formData.contactPerson.email}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Phone *
                            </label>
                            <input
                                type="tel"
                                name="contactPerson.phone"
                                value={formData.contactPerson.phone}
                                onChange={handleInputChange}
                                required
                                pattern="[6-9][0-9]{9}"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Settings */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Settings</h2>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Maximum Job Posts
                            </label>
                            <input
                                type="number"
                                name="settings.maxJobPosts"
                                value={formData.settings.maxJobPosts}
                                onChange={handleInputChange}
                                min="1"
                                max="100"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="settings.allowJobPosting"
                                    checked={formData.settings.allowJobPosting}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Allow Job Posting
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="settings.allowTeamManagement"
                                    checked={formData.settings.allowTeamManagement}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Allow Team Management
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="settings.notificationSettings.emailNotifications"
                                    checked={formData.settings.notificationSettings.emailNotifications}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Email Notifications
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="settings.notificationSettings.smsNotifications"
                                    checked={formData.settings.notificationSettings.smsNotifications}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    SMS Notifications
                                </label>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        {saving ? (
                            <>
                                <LoadingSpinner />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <span>Save Changes</span>
                        )}
                    </button>
                </div>
            </form>

            <Toast />
        </div>
    );
};

export default CompanyProfile;
